import type { Engine } from '../types';
import { buildUrl } from '../url';
import { distanceParam, priceParam } from './shared';

const CONDITION: Record<string, string> = {
  new: '1000',
  openbox: '1500',
  refurb: '2000|2010|2020|2030|2500',
  used: '3000',
  parts: '7000',
};
const SORT: Record<string, string> = {
  low: '15',
  high: '16',
  newest: '10',
  ending: '1',
  nearest: '7',
};
const REGION: Record<string, string> = { us: '1', na: '3', world: '2' };

export const ebay: Engine = {
  id: 'ebay',
  name: 'eBay',
  bang: 'eb',
  aliases: ['ebay'],
  homepage: 'https://www.ebay.com',
  category: 'Shopping',
  defaultOn: true,
  signIn: 'Sold listings need you signed in to eBay.',
  description: 'Listings by format, condition, price, shipping and location.',
  params: [
    {
      id: 'format',
      type: 'choice',
      label: 'Format',
      group: 'Listing',
      options: [
        { value: 'bin', token: 'bin', label: 'Buy It Now' },
        { value: 'auction', token: 'auc', label: 'Auction' },
      ],
    },
    {
      id: 'offers',
      when: { param: 'format', in: ['bin'], orUnset: true },
      whenLabel: 'only applies to Buy It Now listings',
      type: 'flag',
      token: 'bo',
      label: 'Accepts offers',
      group: 'Listing',
    },
    {
      id: 'condition',
      type: 'choice',
      label: 'Condition',
      group: 'Listing',
      options: [
        { value: 'new', token: 'new', label: 'New' },
        { value: 'openbox', token: 'openbox', label: 'Open box' },
        { value: 'refurb', token: 'refurb', label: 'Refurbished' },
        { value: 'used', token: 'used', label: 'Used' },
        { value: 'parts', token: 'parts', label: 'For parts' },
      ],
    },
    {
      id: 'status',
      type: 'choice',
      label: 'Status',
      group: 'Listing',
      help: 'Sold listings show what things actually sell for. eBay requires sign-in for these.',
      options: [
        { value: 'sold', token: 'sold', label: 'Sold' },
        { value: 'ended', token: 'ended', label: 'Ended (sold or not)' },
      ],
    },
    priceParam,
    {
      id: 'freeShipping',
      type: 'flag',
      token: 'fs',
      label: 'Free shipping',
      group: 'Shipping',
    },
    {
      id: 'returns',
      type: 'flag',
      token: 'ret',
      label: 'Returns accepted',
      group: 'Shipping',
    },
    {
      id: 'freeReturns',
      type: 'flag',
      token: 'fret',
      label: 'Free returns',
      group: 'Shipping',
    },
    {
      id: 'region',
      when: { param: 'pickup', unset: true },
      whenLabel: 'can’t be combined with local pickup',
      type: 'choice',
      label: 'Item location',
      group: 'Location',
      options: [
        { value: 'us', token: 'us', label: 'US only' },
        { value: 'na', token: 'na', label: 'North America' },
        { value: 'world', token: 'world', label: 'Worldwide' },
      ],
    },
    distanceParam('Uses the zip from Settings, or eBay guesses from your IP.'),
    {
      id: 'pickup',
      when: { param: 'region', unset: true },
      whenLabel: 'can’t be combined with an item location',
      type: 'flag',
      token: 'local',
      label: 'Local pickup',
      group: 'Location',
      help: 'Within your default distance (or 25 mi) of your zip.',
    },
    {
      id: 'sort',
      type: 'choice',
      label: 'Sort',
      group: 'Sort',
      options: [
        { value: 'low', token: 'low', label: 'Lowest price + shipping' },
        { value: 'high', token: 'high', label: 'Highest price + shipping' },
        { value: 'newest', token: 'newest', label: 'Newly listed' },
        {
          value: 'ending',
          token: 'ending',
          label: 'Ending soonest',
          when: { param: 'status', unset: true },
          whenLabel: 'doesn’t apply to sold or ended listings',
        },
        { value: 'nearest', token: 'nearest', label: 'Nearest first' },
      ],
    },
    {
      id: 'description',
      type: 'flag',
      token: 'desc',
      label: 'Search descriptions',
      group: 'Options',
      help: 'Match words in the description, not just the title.',
    },
    {
      id: 'perPage',
      type: 'flag',
      token: 'max',
      label: '240 results per page',
      group: 'Options',
    },
  ],
  build({ query, values, settings }) {
    const price = typeof values.price === 'object' ? values.price : {};
    const pickup = values.pickup === true;
    const distance =
      typeof values.distance === 'number'
        ? values.distance
        : pickup
          ? (settings.distance ?? 25)
          : undefined;
    const sold = values.status === 'sold';

    return buildUrl('https://www.ebay.com/sch/i.html', {
      _nkw: query,
      LH_BIN: values.format === 'bin',
      LH_Auction: values.format === 'auction',
      LH_BO: values.offers === true,
      LH_ItemCondition: CONDITION[String(values.condition)],
      LH_Sold: sold,
      LH_Complete: sold || values.status === 'ended',
      _udlo: price.min,
      _udhi: price.max,
      LH_FS: values.freeShipping === true,
      LH_RPA: values.returns === true,
      LH_FR: values.freeReturns === true,
      LH_PrefLoc: pickup ? '99' : REGION[String(values.region)],
      LH_LPickup: pickup,
      _stpos: distance !== undefined ? settings.zip : undefined,
      _sadis: distance,
      _fspt: distance !== undefined,
      _sop: SORT[String(values.sort)],
      LH_TitleDesc: values.description === true,
      _ipg: values.perPage === true ? '240' : undefined,
    });
  },
};
